import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import {
  Truck, Gauge, MapTrifold, ReceiptX, UsersThree, Wrench,
  UserPlus, Handshake, SignOut, Sparkle, ChatCircleDots, ChatCircleText, Tray, List, X, Calculator, CreditCard, Rocket, CurrencyDollar, ChartLineUp, ArrowUUpLeft, MagnifyingGlass, Lightning, Money, Heart, ChartBar, MapPin, Newspaper, Bank, Files, ShieldWarning, Bell, Storefront, ClockCounterClockwise, Star, Share, Question, Flask, Confetti, PlugsConnected, PencilSimple, CalendarBlank, GearSix, Vault, FilmSlate, VideoCamera, ClipboardText, ShieldCheck
} from "@phosphor-icons/react";
import { useState, useEffect, useRef } from "react";
import AIPanel from "./AIPanel";
import FeedbackWidget from "../components/FeedbackWidget";
import OnboardingTour from "../components/OnboardingTour";
import TrialBanner from "../components/TrialBanner";
import { BillingGate } from "../components/BillingGate";
import AppInstallNudge from "../components/AppInstallNudge";
import { LeadAlertWatcher } from "../components/LeadAlertWatcher";
import { LeadHotStreak } from "../components/LeadHotStreak";
import { FirstAdLeadBanner } from "../components/FirstAdLeadBanner";

// Role-permission constants — used to hide nav items from members whose
// role doesn't need them. `null` on `roles` = visible to everyone.
const ALL_HANDS = ["owner", "admin", "dispatcher", "ops_manager", "accounting", "viewer"];
const OPS_TEAM = ["owner", "admin", "dispatcher", "ops_manager", "viewer"];  // read/write on dispatch stack
const MGMT_TEAM = ["owner", "admin", "ops_manager"];  // touches settings, hiring, sales
const FINANCE_TEAM = ["owner", "admin", "accounting"];  // billing, payroll, invoices-only view
const FINANCE_TEAM_EXT = ["owner", "admin", "ops_manager", "accounting", "viewer"];  // finance + ops
const OWNER_ADMIN = ["owner", "admin"];

const NAV = [
  { to: "/app/dashboard", label: "Overview", icon: Gauge, testid: "nav-dashboard", roles: ALL_HANDS },
  { to: "/app/playbook", label: "First Week", icon: Rocket, testid: "nav-playbook", roles: ALL_HANDS },
  { to: "/app/authority", label: "New Authority", icon: ClipboardText, testid: "nav-authority", roles: ALL_HANDS },
  { to: "/app/dot-check", label: "DOT Check", icon: ShieldCheck, testid: "nav-dot-check", roles: ALL_HANDS },
  { to: "/app/dispatch", label: "Dispatch", icon: MapTrifold, testid: "nav-dispatch", roles: OPS_TEAM },
  { to: "/app/loadboard", label: "Load Board", icon: Storefront, testid: "nav-loadboard", roles: OPS_TEAM },
  { to: "/app/alerts", label: "Load Alerts", icon: Bell, testid: "nav-alerts", roles: OPS_TEAM },
  { to: "/app/drivers", label: "Drivers", icon: UsersThree, testid: "nav-drivers", roles: OPS_TEAM },
  { to: "/app/paperwork", label: "Paperwork", icon: Files, testid: "nav-paperwork", roles: ALL_HANDS },
  { to: "/app/vault", label: "Docs Vault", icon: Vault, testid: "nav-vault", roles: FINANCE_TEAM_EXT },
  { to: "/app/payroll", label: "Payroll", icon: Money, testid: "nav-payroll", roles: FINANCE_TEAM.concat(["ops_manager"]) },
  { to: "/app/reports/miles", label: "Miles Report", icon: ChartBar, testid: "nav-miles-report", roles: ALL_HANDS },
  { to: "/app/reports/state-mileage", label: "IFTA Miles", icon: MapPin, testid: "nav-state-mileage", roles: ALL_HANDS },
  { to: "/app/reports/lanes", label: "Lane Analysis", icon: MapTrifold, testid: "nav-lane-analysis", roles: OPS_TEAM },
  { to: "/app/broker-rate-card", label: "Rate Card", icon: CurrencyDollar, testid: "nav-broker-rate-card", roles: OPS_TEAM },
  { to: "/app/invoices", label: "Invoices", icon: ReceiptX, testid: "nav-invoices", roles: FINANCE_TEAM_EXT },
  { to: "/app/customer-credit", label: "Customer Credit", icon: ShieldWarning, testid: "nav-customer-credit", roles: FINANCE_TEAM_EXT },
  { to: "/app/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance", roles: OPS_TEAM },
  { to: "/app/recruiting", label: "Recruiting", icon: UserPlus, testid: "nav-recruiting", roles: MGMT_TEAM },
  { to: "/app/leads", label: "Leads", icon: Handshake, testid: "nav-leads", roles: MGMT_TEAM },
  { to: "/app/calculator", label: "Profit Calc", icon: Calculator, testid: "nav-calculator", roles: ALL_HANDS },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", roles: FINANCE_TEAM },
  { to: "/app/team", label: "Team", icon: UsersThree, testid: "nav-team", roles: ALL_HANDS },
  { to: "/app/referrals", label: "Refer & Earn", icon: Share, testid: "nav-referrals", roles: ["owner", "admin", "ops_manager"] },
  { to: "/app/audit", label: "Activity", icon: ClockCounterClockwise, testid: "nav-audit", roles: OWNER_ADMIN },
  { to: "/app/settings/pricing", label: "Rate Rules", icon: CurrencyDollar, testid: "nav-pricing", roles: MGMT_TEAM },
  { to: "/app/integrations", label: "Integrations", icon: PlugsConnected, testid: "nav-integrations", roles: MGMT_TEAM },
];
// Admin sections — the sidebar renders these as collapsible groups when no
// filter is active. When the user types in the search, we flatten and show
// all matches (section headers hide). Order matters: most-frequented sections
// first.
const ADMIN_SECTIONS = [
  {
    id: "content",
    title: "Content & Broker Directory",
    items: [
      { to: "/app/admin/dnp", label: "DNP Moderation", icon: ShieldWarning, testid: "nav-dnp-moderation" },
      { to: "/app/admin/tiktoks", label: "TikTok Reels", icon: FilmSlate, testid: "nav-tiktok-reels" },
    ],
  },
  {
    id: "outreach",
    title: "Cold Outreach",
    items: [
      { to: "/app/admin/leads", label: "Cold Leads", icon: MagnifyingGlass, testid: "nav-cold-leads" },
      { to: "/app/admin/reply-templates", label: "Reply Templates", icon: Files, testid: "nav-reply-templates" },
      { to: "/app/admin/weekly-cohorts", label: "Weekly Cohorts", icon: CalendarBlank, testid: "nav-weekly-cohorts" },
      { to: "/app/admin/suppressions", label: "Suppressions", icon: Sparkle, testid: "nav-suppressions" },
      { to: "/app/admin/founder-letter", label: "Founder Letter", icon: PencilSimple, testid: "nav-founder-letter" },
      { to: "/app/admin/nurture", label: "Trial Nurture", icon: Heart, testid: "nav-nurture" },
      { to: "/app/admin/waitlist", label: "Waitlist", icon: Sparkle, testid: "nav-waitlist" },
      { to: "/app/admin/signup-review", label: "Signup Review", icon: ShieldWarning, testid: "nav-signup-review" },
      { to: "/app/admin/feedback", label: "Feedback", icon: ChatCircleDots, testid: "nav-feedback" },
    ],
  },
  {
    id: "growth",
    title: "Growth & Marketing",
    items: [
      { to: "/app/admin/pilot-cohort", label: "Pilot Cohort", icon: Sparkle, testid: "nav-pilot-cohort" },
      { to: "/app/admin/pilots", label: "Pilot Access", icon: Rocket, testid: "nav-pilots" },
      { to: "/app/admin/pilot-drip", label: "Pilot Drip", icon: Sparkle, testid: "nav-pilot-drip" },
      { to: "/app/admin/free-workflow", label: "Free Workflows", icon: Sparkle, testid: "nav-free-workflow" },
      { to: "/app/admin/ad-studio", label: "Ad Studio", icon: Confetti, testid: "nav-ad-studio" },
      { to: "/app/admin/sharing-leaderboard", label: "Sharing Leaderboard", icon: Star, testid: "nav-sharing-leaderboard" },
      { to: "/app/admin/testimonials", label: "Testimonials", icon: Star, testid: "nav-testimonials" },
      { to: "/app/admin/partners", label: "Partner Program", icon: Handshake, testid: "nav-partners" },
      { to: "/app/admin/win-back", label: "Win-Back", icon: ArrowUUpLeft, testid: "nav-winback" },
    ],
  },
  {
    id: "analytics",
    title: "Analytics",
    items: [
      { to: "/app/admin/founder-dashboard", label: "Founder KPIs", icon: ChartLineUp, testid: "nav-founder-dashboard" },
      { to: "/app/admin/launch", label: "Launch Checklist", icon: Rocket, testid: "nav-launch" },
      { to: "/app/admin/metrics", label: "Metrics", icon: ChartLineUp, testid: "nav-metrics" },
      { to: "/app/admin/activation", label: "Activation", icon: Lightning, testid: "nav-activation" },
      { to: "/app/admin/experiments", label: "A/B Tests", icon: Flask, testid: "nav-experiments" },
      { to: "/app/admin/coaching-resets", label: "Coaching Audit", icon: ChatCircleText, testid: "nav-coaching-resets" },
      { to: "/app/admin/digest", label: "Weekly Digest", icon: Newspaper, testid: "nav-digest" },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    items: [
      { to: "/app/inbox", label: "Inbox", icon: Tray, testid: "nav-inbox" },
      { to: "/app/admin/manual-payments", label: "Manual Payments", icon: Bank, testid: "nav-manual-payments" },
      { to: "/app/admin/integrations", label: "Integrations", icon: PlugsConnected, testid: "nav-admin-integrations" },
      { to: "/app/admin/roster", label: "Admin Roster", icon: UsersThree, testid: "nav-admin-roster" },
      { to: "/app/admin/va-onboarding", label: "Delegate to VA", icon: Rocket, testid: "nav-va-onboarding" },
      { to: "/app/admin/va-activity", label: "VA Activity", icon: ClockCounterClockwise, testid: "nav-va-activity" },
      { to: "/app/admin/va-bonus", label: "VA Bonus", icon: CurrencyDollar, testid: "nav-va-bonus" },
    ],
  },
];
// Flat list for search + backwards compat with previous code
const ADMIN_NAV = ADMIN_SECTIONS.flatMap((s) => s.items);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  // Close mobile nav on route change
  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  const role = user?.role || "owner";
  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(role));
  const currentLabel = [...visibleNav, ...ADMIN_NAV].find((n) => n.to === location.pathname)?.label || "";
  return (
    <div className={`min-h-screen lg:flex noise-bg relative ${user?.is_demo_user ? "pt-10" : ""}`} data-testid="app-layout">
      {/* Demo mode banner */}
      {user?.is_demo_user && (
        <div
          data-testid="demo-banner"
          className="fixed top-0 left-0 right-0 z-40 bg-[#FF5A1F] text-black text-xs sm:text-sm py-2 px-3 flex items-center justify-center gap-2 sm:gap-3 font-medium text-center"
        >
          <Sparkle size={14} weight="fill" className="hidden sm:block" />
          <span className="truncate">Live demo — data resets periodically.</span>
          <button
            data-testid="demo-signup-btn"
            onClick={() => { logout(); nav("/signup"); }}
            className="underline hover:no-underline font-semibold whitespace-nowrap"
          >
            Sign up →
          </button>
        </div>
      )}

      {/* Wave 12: force password change for invited admins */}
      {user?.must_change_password && !location.pathname.includes("/account/settings") && (
        <div
          data-testid="must-change-password-banner"
          className="fixed top-0 left-0 right-0 z-40 bg-amber-500 text-black text-xs sm:text-sm py-2 px-3 flex items-center justify-center gap-3 font-medium text-center"
          style={user?.is_demo_user ? { top: 40 } : {}}
        >
          <ShieldWarning size={14} weight="fill" />
          <span>Rotate your temporary invite password now.</span>
          <Link
            to="/app/account/settings"
            className="underline hover:no-underline font-semibold whitespace-nowrap"
          >
            Change it →
          </Link>
        </div>
      )}

      {/* Wave 14: Read-only admin banner */}
      {user?.is_readonly_admin && (
        <div
          data-testid="readonly-admin-banner"
          className="fixed top-0 left-0 right-0 z-30 bg-purple-500/95 text-white text-xs py-1.5 px-3 flex items-center justify-center gap-2 font-medium text-center"
          style={{
            top: (user?.is_demo_user ? 40 : 0) + (user?.must_change_password ? 40 : 0),
          }}
        >
          <ShieldWarning size={12} weight="fill" />
          <span>Read-only admin — view-only across the app. You <strong className="font-semibold">can</strong> reply to hot leads in the inbox, but can't send cold outreach or manage admins.</span>
        </div>
      )}

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0A0A0A]"
        style={user?.is_demo_user ? { top: 40 } : {}}
      >
        <div className="flex items-center gap-3">
          <Logo variant="sm" />
          {currentLabel && <div className="text-[10px] text-neutral-500 tracking-widest uppercase border-l border-white/10 pl-3">{currentLabel}</div>}
          {user?.is_admin && <LeadHotStreak compact />}
        </div>
        <button
          data-testid="mobile-nav-toggle"
          onClick={() => setMobileNav(true)}
          className="w-10 h-10 rounded-md border border-white/10 flex items-center justify-center hover:border-white/25 transition-colors"
        >
          <List size={20} weight="bold" />
        </button>
      </header>

      {/* Mobile drawer overlay */}
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="flex-1 bg-black/60" onClick={() => setMobileNav(false)} />
          <aside className="w-72 max-w-[85vw] bg-[#0A0A0A] border-l border-white/10 flex flex-col h-full">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <Logo />
              <button
                data-testid="mobile-nav-close"
                onClick={() => setMobileNav(false)}
                className="text-neutral-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarNav user={user} onAI={() => { setAiOpen(true); setMobileNav(false); }} onLogout={() => { logout(); nav("/login"); }} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-white/10 bg-[#0A0A0A] flex-col relative z-10">
        <div className="p-6 border-b border-white/10">
          <Logo />
          <div className="text-[10px] text-neutral-500 tracking-widest uppercase mt-2">Ops Copilot</div>
          {user?.is_admin && <LeadHotStreak className="mt-3" />}
        </div>
        <SidebarNav user={user} onAI={() => setAiOpen(true)} onLogout={() => { logout(); nav("/login"); }} />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 relative z-10">
        <TrialBanner />
        {user?.is_admin && <FirstAdLeadBanner />}
        {user?.is_admin && <LeadAlertWatcher />}
        <AppInstallNudge />
        <BillingGate><Outlet /></BillingGate>
      </main>

      {/* AI Panel */}
      <AIPanel open={aiOpen} onClose={() => setAiOpen(false)} />

      {/* Floating AI toggle */}
      <button
        data-testid="floating-ai-btn"
        onClick={() => setAiOpen(true)}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-[#007AFF] hover:bg-[#3392FF] flex items-center justify-center shadow-lg z-30 transition-colors"
        title="Ask AI Copilot"
      >
        <ChatCircleDots size={22} weight="fill" color="white" />
      </button>

      {/* Floating pilot feedback widget — sits above the AI button */}
      <FeedbackWidget />

      {/* First-login product tour (no-op if user already completed/skipped) */}
      {!user?.needs_checkout && <OnboardingTour />}
    </div>
  );
}

function SidebarNav({ user, onAI, onLogout }) {
  const location = useLocation();
  const role = user?.role || "owner";
  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(role));
  const [filter, setFilter] = useState("");
  const isOnAdminRoute = location.pathname.startsWith("/app/admin");
  // Admin section starts EXPANDED whenever the user is browsing an admin
  // page — otherwise defaults to expanded too so nothing hides on load.
  // The user's #1 pain was "I only see 3 admin links" → we ALWAYS start
  // expanded and add a visible count badge so they know 23 exist.
  const [adminOpen, setAdminOpen] = useState(true);
  const adminSectionRef = useRef(null);

  // Auto-scroll the sidebar to the ADMIN section when the user lands on
  // any /app/admin/* route. Solves the "hidden below the fold" problem.
  useEffect(() => {
    if (isOnAdminRoute && adminSectionRef.current) {
      adminSectionRef.current.scrollIntoView({ block: "start", behavior: "auto" });
    }
  }, [isOnAdminRoute, location.pathname]);

  const q = filter.trim().toLowerCase();
  const filteredNav = q
    ? visibleNav.filter((n) => n.label.toLowerCase().includes(q))
    : visibleNav;
  const filteredAdmin = q
    ? ADMIN_NAV.filter((n) => n.label.toLowerCase().includes(q))
    : ADMIN_NAV;

  return (
    <>
      {/* Sticky search filter — the single biggest UX win. Type "cold" and
          Cold Leads surfaces instantly regardless of how deep it sits. */}
      <div className="px-3 pt-4 pb-2 border-b border-white/5">
        <div className="relative">
          <MagnifyingGlass
            size={14}
            weight="bold"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
          />
          <input
            data-testid="sidebar-nav-filter"
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search menu…"
            className="w-full text-xs bg-[#141414] border border-white/10 rounded-md py-1.5 pl-7 pr-6 text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF5A1F]/60"
          />
          {filter && (
            <button
              data-testid="sidebar-nav-filter-clear"
              onClick={() => setFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
              aria-label="Clear filter"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto sidebar-scroll" data-testid="sidebar-nav">
        {filteredNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#141414] text-white border-l-2 border-[#FF5A1F]"
                  : "text-neutral-400 hover:text-white hover:bg-[#141414]"
              }`
            }
          >
            <n.icon size={17} weight="regular" />
            {n.label}
          </NavLink>
        ))}
        {q && filteredNav.length === 0 && !user?.is_admin && (
          <div className="px-3 py-3 text-xs text-neutral-500" data-testid="sidebar-nav-empty">
            No matches
          </div>
        )}
        {user?.is_admin && (
          <div ref={adminSectionRef}>
            <button
              type="button"
              data-testid="sidebar-admin-toggle"
              onClick={() => setAdminOpen((v) => !v)}
              className="w-full mt-3 flex items-center justify-between px-3 py-1.5 rounded-md text-[10px] font-semibold tracking-[0.14em] text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <span className={`inline-block transition-transform text-[8px] ${adminOpen ? "rotate-90" : ""}`}>▶</span>
                ADMIN
              </span>
              <span
                className="text-[9px] font-medium bg-[#FF5A1F]/15 text-[#FF5A1F] px-1.5 py-0.5 rounded-full"
                data-testid="sidebar-admin-count"
              >
                {filteredAdmin.length}{q ? "" : ""}
              </span>
            </button>
            {adminOpen && (
              <div className="space-y-0.5">
                {q ? (
                  // While filtering, render flat matches (section headers
                  // would just add noise when the user is targeting one item).
                  <>
                    {filteredAdmin.map((n) => (
                      <AdminNavItem key={n.to} n={n} />
                    ))}
                    {filteredAdmin.length === 0 && filteredNav.length === 0 && (
                      <div className="px-3 py-2 text-xs text-neutral-500" data-testid="sidebar-nav-empty">
                        No matches
                      </div>
                    )}
                  </>
                ) : (
                  // No filter → grouped sections with sub-headers.
                  ADMIN_SECTIONS.map((section) => (
                    <AdminSectionGroup key={section.id} section={section} />
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-white/10">
        <button
          data-testid="open-ai-copilot-btn"
          onClick={onAI}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-[#141414] hover:bg-[#1B1B1B] border border-white/10 text-sm transition-colors"
        >
          <Sparkle size={16} weight="fill" color="#007AFF" />
          <span>AI Copilot</span>
          <span className="ml-auto pill pill-info">GPT-5.2</span>
        </button>
        <button
          data-testid="replay-tour-btn"
          onClick={() => window.dispatchEvent(new CustomEvent("open-onboarding-tour"))}
          className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-md text-xs text-neutral-500 hover:text-white hover:bg-[#141414] transition-colors"
          title="Replay product tour"
        >
          <Question size={14} weight="regular" />
          <span>Replay product tour</span>
        </button>
      </div>

      <div className="p-3">
        {/* Role + account context badge — helps multi-user teams know
            whose account they're operating in right now. */}
        {user?.role && (
          <div
            className="mb-2 px-2 py-2 rounded-md bg-white/[0.02] border border-white/[0.06] flex items-center gap-2"
            data-testid="sidebar-role-badge"
          >
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              user.is_owner ? "bg-[#FF5A1F]" : "bg-emerald-400"
            }`} />
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 leading-none">
                {user.is_owner ? "OWNER" : (user.role || "member").toUpperCase()}
              </div>
              <div className="text-[10px] text-neutral-300 mt-0.5 truncate" title={user.company_name || ""}>
                {user.company_name || "your account"}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center text-xs font-semibold shrink-0">
            {user?.full_name?.[0] || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate" data-testid="current-user-name">{user?.full_name}</div>
            <div className="text-[10px] text-neutral-500 truncate">{user?.company_name}</div>
          </div>
          <Link
            to="/app/account/settings"
            data-testid="account-settings-link"
            title="Account settings"
            className="text-neutral-500 hover:text-white shrink-0"
          >
            <GearSix size={16} />
          </Link>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="text-neutral-500 hover:text-white shrink-0"
            title="Sign out"
          >
            <SignOut size={16} />
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// AdminNavItem — single admin link (compact styling). Extracted so both
// the flat filtered view AND the sectioned view can share the same DOM.
// ---------------------------------------------------------------------------
function AdminNavItem({ n }) {
  return (
    <NavLink
      to={n.to}
      data-testid={n.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
          isActive
            ? "bg-[#141414] text-white border-l-2 border-[#FF5A1F]"
            : "text-neutral-400 hover:text-white hover:bg-[#141414]"
        }`
      }
    >
      <n.icon size={15} weight="regular" />
      {n.label}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// AdminSectionGroup — collapsible sub-section header + its items. Each
// section remembers its open/closed state via a per-section local key.
// The "Cold Outreach" section starts EXPANDED (most-used); others closed
// to reduce cognitive load — user can click the header to open.
// ---------------------------------------------------------------------------
function AdminSectionGroup({ section }) {
  const location = useLocation();
  // Auto-expand if any item in this section is the active route
  const hasActive = section.items.some((it) => location.pathname === it.to);
  // Default state: expand the always-important Content & Cold Outreach
  // groups; auto-expand any group with an active route so users see their
  // context. Content sits at the top so DNP + TikTok links are visible on
  // first render — most-used features shouldn't require an extra click.
  const [open, setOpen] = useState(
    section.id === "content" || section.id === "outreach" || hasActive
  );

  return (
    <div className="mt-2" data-testid={`admin-section-${section.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`admin-section-toggle-${section.id}`}
        className="w-full flex items-center justify-between px-3 py-1 rounded text-[9px] font-semibold tracking-[0.18em] text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.02] uppercase transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <span className={`inline-block transition-transform text-[7px] ${open ? "rotate-90" : ""}`}>▶</span>
          {section.title}
        </span>
        <span className="text-[9px] font-medium bg-white/[0.04] border border-white/10 text-neutral-500 px-1.5 rounded-full">
          {section.items.length}
        </span>
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5 pl-2 border-l border-white/[0.06] ml-2">
          {section.items.map((n) => (
            <AdminNavItem key={n.to} n={n} />
          ))}
        </div>
      )}
    </div>
  );
}

